import React, { memo } from "react";
import IconMC from "../../../components/IconMC";
import MessageStatusMC from "./MessageStatusMC";

/**
 * FlowMessageMC - Displays a WhatsApp Flow message sent from the inbox.
 * Shows the flow name/header, body text, a CTA button preview, and message status.
 * Also displays Flow Replies (nfm_reply) by parsing the response JSON.
 */
const FlowMessageMC = memo(({ message, conversation }) => {
  const flow = message.contentFlow || {};

  const {
    header, body, flow_cta, footer, is_reply, response_json
  } = flow;

  if (is_reply) {
    let formattedResponses = [];
    if (response_json) {
      try {
        const parsed = JSON.parse(response_json);

        const formatKey = (str) => {
          if (!str) return '';
          return str
            .replace(/^screen_\d+_/, '') // Remove prefix like "screen_0_"
            .replace(/_\d+$/, '')        // Remove suffix like "_1"
            .replace(/_/g, ' ');         // Replace underscores with spaces
        };

        const formatValue = (val) => {
          if (Array.isArray(val)) {
            return val
              .map((item) => {
                if (item && typeof item === 'object') {
                  return item.file_name || item.filename || item.name || (item.id ? String(item.id) : JSON.stringify(item));
                }
                return String(item);
              })
              .join(', ');
          }
          if (val && typeof val === 'object') {
            return val.file_name || val.filename || val.name || (val.id ? String(val.id) : JSON.stringify(val));
          }
          if (typeof val !== 'string') return String(val);
          return val.replace(/^\d+_/, '').replace(/_/g, ' '); // "0_Excellent" -> "Excellent"
        };

        formattedResponses = Object.entries(parsed)
          .filter(([k]) => k !== 'flow_token')
          .map(([k, v]) => ({ key: formatKey(k), value: formatValue(v) }));
      } catch (e) {
        // ignore JSON parse error
      }
    }

    return (
      <div className="tw-wrapper tw-text-sm tw-min-w-[220px]">
        {/* Flow Reply Header */}
        <div className="tw-flex tw-items-center tw-gap-1 tw-mb-2 tw-font-semibold tw-text-gray-800">
          <IconMC name="whatsapp-fill-outline" size="sm" className="tw-text-green-500" />
          <span>Respuesta de Flow</span>
        </div>

        {/* Flow body (skip if it's just 'Sent') */}
        {body && body !== 'Sent' && (
          <p className="tw-text-gray-700 tw-whitespace-pre-line tw-mb-2 tw-text-xs tw-font-semibold">
            {body}
          </p>
        )}

        {/* Parsed JSON answers */}
        {formattedResponses.length > 0 && (
          <div className="tw-bg-gray-50 tw-p-3 tw-rounded-lg tw-border tw-border-gray-200 tw-text-xs tw-text-gray-700 tw-mb-2">
            <p className="tw-font-semibold tw-text-gray-800 tw-mb-2">Respuestas enviadas:</p>
            <div className="tw-flex tw-flex-col tw-gap-2">
              {formattedResponses.map((item, i) => (
                <div key={i} className="tw-break-words">
                  <p className="tw-text-[10px] tw-text-gray-500 tw-font-medium tw-leading-tight tw-mb-0.5 tw-capitalize">
                    {item.key}
                  </p>
                  <p className="tw-text-xs tw-text-gray-800 tw-font-medium">
                    {item.value}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        <MessageStatusMC message={message} platform={conversation?.platform} />
      </div>
    );
  }

  return (
    <div className="tw-wrapper tw-text-sm tw-min-w-[220px]">
      {/* Flow header */}
      <div className="tw-flex tw-items-center tw-gap-1 tw-mb-1 tw-font-semibold tw-text-gray-800">
        <IconMC name="whatsapp-fill-outline" size="sm" className="tw-text-green-500" />
        <span>{header}</span>
      </div>

      {/* Flow body */}
      {body && (
        <p className="tw-text-gray-700 tw-whitespace-pre-line tw-mb-2 tw-text-xs">
          {body}
        </p>
      )}

      {/* Footer */}
      {footer && (
        <p className="tw-text-gray-700 tw-whitespace-pre-line tw-mb-2 tw-text-xs">
          {footer}
        </p>
      )}

      {/* CTA button preview */}
      <div
        className="tw-border tw-border-green-400 tw-rounded-lg tw-px-3 tw-py-1 tw-text-center tw-text-green-600 tw-text-xs tw-font-medium tw-bg-green-50 tw-cursor-pointer hover:tw-bg-green-100 tw-transition-colors"
        onClick={() => {
          if (window.openSidebarMC && flow.flow_id) {
            window.openSidebarMC('WhatsappFlowDetailModal', {
              flow: { meta_flow_id: flow.flow_id, name: header },
              className: 'tw-w-[75vw] tw-max-w-[1200px]'
            });
          }
        }}
      >
        {flow_cta}
      </div>

      <MessageStatusMC message={message} platform={conversation?.platform} />
    </div>
  );
});

FlowMessageMC.displayName = 'FlowMessageMC';

export default FlowMessageMC;
